import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  calculateMonthlyPayment,
  calculateEarlyRepaymentScenarios,
  calculateAPR,
  calculateEffectiveAPR,
  convertMonthlyToAnnualRate
} from '../../utils/calculations';
import * as Sentry from '@sentry/browser';

const EarlyRepaymentCalculator = ({ onCalculate }) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      currentBalance: 5000,
      monthlyInterestRate: 3.99,
      remainingMonths: 24,
      currentMonthlyPayment: '',
      earlyPaymentAmount: 0,
      scenarioType: 'both'
    }
  });

  // Watch values for real-time calculations
  const watchedValues = watch();
  const { currentBalance, monthlyInterestRate, remainingMonths } = watchedValues;

  // Convert monthly interest rate to annual interest rate
  const calculatedAnnualRate = convertMonthlyToAnnualRate(monthlyInterestRate);

  // Calculate current monthly payment for display
  const calculateCurrentPayment = () => {
    if (!currentBalance || !monthlyInterestRate || !remainingMonths) return 0;
    
    try {
      const annualRate = convertMonthlyToAnnualRate(monthlyInterestRate);
      const payment = calculateMonthlyPayment(
        parseFloat(currentBalance),
        annualRate,
        parseInt(remainingMonths, 10)
      );
      return payment.toNumber();
    } catch {
      return 0;
    }
  };

  const calculatedCurrentPayment = calculateCurrentPayment();

  const onSubmit = async (data) => {
    try {
      setIsCalculating(true);
      console.log('Calculating early repayment with:', data);
      
      // Parse form values
      const currentBalance = parseFloat(data.currentBalance);
      const monthlyInterestRate = parseFloat(data.monthlyInterestRate);
      const annualInterestRate = convertMonthlyToAnnualRate(monthlyInterestRate);
      const remainingMonths = parseInt(data.remainingMonths, 10);
      const earlyPaymentAmount = parseFloat(data.earlyPaymentAmount || 0);
      const scenarioType = data.scenarioType;
      
      // Validate inputs
      if (isNaN(currentBalance) || currentBalance <= 0) {
        alert('Please enter a valid current balance.');
        return;
      }
      
      if (isNaN(monthlyInterestRate) || monthlyInterestRate < 0) {
        alert('Please enter a valid monthly interest rate.');
        return;
      }
      
      if (isNaN(remainingMonths) || remainingMonths <= 0) {
        alert('Please enter a valid remaining term.');
        return;
      }
      
      if (isNaN(earlyPaymentAmount) || earlyPaymentAmount < 0) {
        alert('Please enter a valid early payment amount (or 0 for full settlement only).');
        return;
      }
      
      if (earlyPaymentAmount >= currentBalance) {
        alert('Early payment amount cannot be greater than or equal to the current balance. For full settlement, set early payment to 0.');
        return;
      }
      
      // Use provided current payment or calculate it
      let currentMonthlyPayment;
      const currentPaymentStr = (data.currentMonthlyPayment || '').toString().trim();
      if (currentPaymentStr && parseFloat(currentPaymentStr) > 0) {
        currentMonthlyPayment = parseFloat(currentPaymentStr);
      } else {
        currentMonthlyPayment = calculateMonthlyPayment(currentBalance, annualInterestRate, remainingMonths).toNumber();
      }

      console.log('Calculation parameters:', {
        currentBalance,
        annualInterestRate,
        remainingMonths,
        currentMonthlyPayment,
        earlyPaymentAmount,
        scenarioType
      });

      // Calculate early repayment scenarios
      const results = calculateEarlyRepaymentScenarios(
        currentBalance,
        annualInterestRate,
        remainingMonths,
        currentMonthlyPayment,
        earlyPaymentAmount,
        scenarioType
      );

      console.log('Raw calculation results:', results);

      // Calculate APR
      const apr = calculateAPR(currentBalance, annualInterestRate, remainingMonths);

      // Prepare final results
      const finalResults = {
        calculatorType: 'early',
        currentBalance,
        monthlyInterestRate,
        annualInterestRate,
        apr,
        remainingMonths,
        currentMonthlyPayment,
        earlyPaymentAmount,
        scenarioType,
        ...results
      };

      console.log('Early repayment calculation results:', finalResults);
      
      // Pass results to parent component
      onCalculate(finalResults);
    } catch (error) {
      console.error('Error calculating early repayment:', error);
      console.error('Error stack:', error.stack);
      
      // Capture the error in Sentry with additional context
      Sentry.captureException(error, {
        tags: {
          component: 'EarlyRepaymentCalculator',
          calculatorType: 'early'
        },
        extra: {
          formData: data,
          currentBalance: data.currentBalance,
          monthlyInterestRate: data.monthlyInterestRate,
          remainingMonths: data.remainingMonths,
          earlyPaymentAmount: data.earlyPaymentAmount,
          scenarioType: data.scenarioType
        }
      });
      
      alert(`Error: ${error.message || 'An error occurred during calculation. Please check your input values.'}`);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="card">
      <h2 className="section-title">Early Repayment Calculator</h2>
      <p className="mb-4 text-gray-600">
        Calculate full settlement amounts and partial early repayment scenarios. See how making an early payment affects your monthly payments or loan term.
      </p>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Outstanding Balance (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="0.01"
              {...register("currentBalance", { 
                required: "Current balance is required", 
                min: { value: 1, message: "Amount must be positive" },
                valueAsNumber: true
              })}
            />
            {errors.currentBalance && (
              <p className="mt-1 text-sm text-red-600">{errors.currentBalance.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Interest Rate (%)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="0"
              step="0.01"
              {...register("monthlyInterestRate", { 
                required: "Monthly interest rate is required",
                min: { value: 0, message: "Rate must be 0 or positive" },
                valueAsNumber: true
              })}
            />
            {errors.monthlyInterestRate && (
              <p className="mt-1 text-sm text-red-600">{errors.monthlyInterestRate.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Annual equivalent: {calculatedAnnualRate.toFixed(2)}%
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remaining Loan Term (months)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="1"
              {...register("remainingMonths", { 
                required: "Remaining term is required",
                min: { value: 1, message: "Term must be at least 1 month" },
                valueAsNumber: true
              })}
            />
            {errors.remainingMonths && (
              <p className="mt-1 text-sm text-red-600">{errors.remainingMonths.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Monthly Payment (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="0"
              step="0.01"
              placeholder={`Calculated: £${calculatedCurrentPayment.toFixed(2)}`}
              {...register("currentMonthlyPayment", {
                valueAsNumber: true
              })}
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave blank to use calculated payment (£{calculatedCurrentPayment.toFixed(2)})
            </p>
          </div>
          
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Early Payment Amount (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="0"
              step="0.01"
              {...register("earlyPaymentAmount", { 
                min: { value: 0, message: "Amount must be 0 or positive" },
                valueAsNumber: true
              })}
            />
            {errors.earlyPaymentAmount && (
              <p className="mt-1 text-sm text-red-600">{errors.earlyPaymentAmount.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              <strong>Set to 0</strong> to see <strong>full settlement calculation only</strong>. Enter a partial amount (less than the balance) to see both full settlement and partial early repayment scenarios.
            </p>
          </div>
          
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scenarios to Calculate
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative border rounded-md px-4 py-3 flex items-center cursor-pointer">
                <input
                  id="scenario-both"
                  type="radio"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  value="both"
                  {...register("scenarioType")}
                />
                <label htmlFor="scenario-both" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                  Both Scenarios
                </label>
              </div>
              
              <div className="relative border rounded-md px-4 py-3 flex items-center cursor-pointer">
                <input
                  id="scenario-reduce-payment"
                  type="radio"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  value="reducePayment"
                  {...register("scenarioType")}
                />
                <label htmlFor="scenario-reduce-payment" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                  Lower Payments
                </label>
              </div>
              
              <div className="relative border rounded-md px-4 py-3 flex items-center cursor-pointer">
                <input
                  id="scenario-reduce-term"
                  type="radio"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  value="reduceTerm"
                  {...register("scenarioType")}
                />
                <label htmlFor="scenario-reduce-term" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                  Shorter Term
                </label>
              </div>
            </div>
          </div>
        </div>
        
        {/* Current Loan Summary */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Current Loan Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-600 font-medium">Outstanding Balance</p>
              <p className="text-lg font-semibold text-blue-700">£{(currentBalance || 0).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Monthly Payment</p>
              <p className="text-lg font-semibold text-blue-700">£{calculatedCurrentPayment.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Remaining Term</p>
              <p className="text-lg font-semibold text-blue-700">{remainingMonths || 0} months</p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Annual Interest Rate</p>
              <p className="text-lg font-semibold text-blue-700">{calculatedAnnualRate.toFixed(2)}%</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
          <div className="flex">
            <div className="shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>Early repayment scenarios explained:</strong>
              </p>
              <ul className="mt-1 text-sm text-green-700 list-disc list-inside">
                <li><strong>Lower Payments:</strong> Keep the same loan term but reduce monthly payments with the early payment.</li>
                <li><strong>Shorter Term:</strong> Keep the same monthly payment but pay off the loan faster with the early payment.</li>
                <li><strong>Full Settlement:</strong> Pay off the entire loan today and see total interest savings.</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <button 
            type="submit" 
            className="btn btn-primary cursor-pointer"
            disabled={isCalculating}
          >
            {isCalculating ? 'Calculating...' : 'Calculate Early Repayment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EarlyRepaymentCalculator;